import clsx from "clsx";
import { Check, CircleAlert, Loader2 } from "lucide-react";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useId, useMemo, useState } from "react";
import { alreadyShown, flash, markShown } from "../lib/flash";
import { useReportSave } from "./saving";

/*
 * Fields that save themselves, like ServiceM8's: type, then leave the field (or press Enter) and
 * it saves; a tick shows it saved (beside the field, or for a whole record window in its title
 * bar: see saving.tsx), and ServiceM8's reason shows under it if it was turned down
 * (the field keeps what was typed, to fix and try again). Toggles and selects save straight away.
 * `onSave` returns a promise that rejects with the reason.
 *
 * Sizes come from the field variables in styles.css (--field-text, --label-text...): roomy on
 * pages, compact inside record windows.
 */

type SaveState = { kind: "idle" } | { kind: "saving" } | { kind: "saved" } | { kind: "error"; message: string };

/**
 * Inside: the fields edit a draft (saved by its own Save button), so they never say "Saving" or "Saved" (DraftFields). Each says
 * whether what's typed in it can't be taken (`report`), so the draft isn't saved with what it had before.
 */
const DraftContext = createContext<{ report: (id: string, invalid: boolean) => void; invalid: boolean } | null>(null);

/**
 * Fields that change a draft, not the record: nothing's saved until the draft's Save, so they don't say it is (no "Saving…" or
 * "Saved", beside them or in the window's title bar). What's typed being turned down still shows under it, and its Save can't
 * be pressed meanwhile: inside, `useDraftInvalid`; outside, `onInvalid`.
 */
export function DraftFields({ children, onInvalid }: Readonly<{ children: ReactNode; onInvalid?: (invalid: boolean) => void }>) {
    const [bad, setBad] = useState<ReadonlySet<string>>(() => new Set());
    const invalid = bad.size > 0;
    useEffect(() => onInvalid?.(invalid), [invalid, onInvalid]);
    const report = useCallback(
        (id: string, now: boolean) =>
            setBad((was) => {
                if (was.has(id) === now) return was;
                const next = new Set(was);
                if (now) next.add(id);
                else next.delete(id);
                return next;
            }),
        [],
    );
    const value = useMemo(() => ({ invalid, report }), [invalid, report]);
    return <DraftContext value={value}>{children}</DraftContext>;
}

/** Inside DraftFields: what's typed in one of its fields can't be taken (its draft has what it had before), so it can't be saved yet. */
export const useDraftInvalid = () => useContext(DraftContext)?.invalid ?? false;

type Save = ReturnType<typeof useSave>;

function useSave(onSave: (value: string) => Promise<unknown>) {
    const [state, setState] = useState<SaveState>({ kind: "idle" });
    const draft = useContext(DraftContext) !== null;
    useEffect(() => {
        if (state.kind !== "saved") return;
        const t = setTimeout(() => setState({ kind: "idle" }), 1500);
        return () => clearTimeout(t);
    }, [state]);
    const run = async (value: string) => {
        // A draft's field: nothing's saved yet, so no "Saving" or "Saved" (only what was turned down).
        setState({ kind: draft ? "idle" : "saving" });
        try {
            await onSave(value);
            setState({ kind: draft ? "idle" : "saved" });
            return true;
        } catch (err) {
            setState({ kind: "error", message: err instanceof Error ? err.message : String(err) });
            return false;
        }
    };
    // Inside a record window the window shows how saves are going, not each field.
    const reported = useReportSave(state.kind);
    return { state, reported, run, clear: () => setState({ kind: "idle" }) };
}

/**
 * Saving / saved, beside a field's label, unless the area around shows it (a record window). Its
 * space is always kept (empty when idle), so nothing moves as a field saves.
 */
function SaveMark({ save }: Readonly<{ save: Save }>) {
    if (save.reported) return null;
    const { state } = save;
    let mark: ReactNode = null;
    if (state.kind === "saving") mark = <Loader2 className="size-3.5 animate-spin text-muted" aria-label="Saving" />;
    if (state.kind === "saved") mark = <Check className="size-3.5 text-brand-600" aria-label="Saved" />;
    return <span className="inline-flex size-3.5 shrink-0">{mark}</span>;
}

/** Label row and messages around a control. */
function Frame(
    props: Readonly<{ id: string; label: string; hideLabel?: boolean; hint?: ReactNode; save: Save; error?: string | null; className?: string; labelAside?: ReactNode; children: ReactNode }>,
) {
    const error = props.error ?? (props.save.state.kind === "error" ? props.save.state.message : null);
    return (
        <div className={clsx("space-y-1", props.className)}>
            {props.hideLabel ? (
                // Said by what's beside it (a switch, a column): for screen readers only. Before the field: Tailwind v4's
                // space-y puts its gap under every child but the last, so after it, it would leave a gap under the field.
                <label htmlFor={props.id} className="sr-only">
                    {props.label}
                </label>
            ) : (
                <div className="flex items-center gap-2">
                    <label htmlFor={props.id} className="field-label">
                        {props.label}
                    </label>
                    <SaveMark save={props.save} />
                    {props.labelAside ? <span className="ml-auto">{props.labelAside}</span> : null}
                </div>
            )}
            {props.children}
            {error ? (
                <p className="flex items-start gap-1 text-(length:--label-text) text-danger" role="alert">
                    <CircleAlert className="mt-px size-3.5" />
                    {error}
                </p>
            ) : null}
            {!error && props.hint ? <p className="text-(length:--label-text) text-muted">{props.hint}</p> : null}
        </div>
    );
}

export interface TextFieldProps {
    label: string;
    value: string;
    onSave: (value: string) => Promise<unknown>;
    disabled?: boolean;
    placeholder?: string;
    /** Under it; a function is given what is typed so far (e.g. the amount worked out another way, as it is typed). */
    hint?: ReactNode | ((typed: string) => ReactNode);
    multiline?: boolean;
    /** Checked before saving: a message stops the save and shows. */
    validate?: (value: string) => string | null;
    /** Shown inside the field, before and after the value (e.g. "$" and "inc GST"). */
    prefix?: ReactNode;
    suffix?: ReactNode;
    inputMode?: "text" | "decimal" | "numeric";
    /** No visible label (what's beside it says what it is); screen readers still get `label`. */
    hideLabel?: boolean;
    /** Focused with its text selected when it first shows (e.g. a name just made up, to type over). */
    selectOnOpen?: boolean;
    /** A warning is about it (e.g. "Every item needs an item number"): highlighted until it's fixed. */
    warning?: boolean;
    /** Each new value takes the cursor to it (and scrolls it into view): a warning's button ("Set it"), pressed again and again. */
    focusToken?: number;
    /**
     * Given each change as it's typed (when it's valid: not while half-typed), not only when it's left: for a field that's part of a
     * draft (nothing saved until its Save), so what it causes (its save bar, the price it gives) shows at once.
     */
    live?: boolean;
    /** On its label's line, on the right (e.g. something known about it), as SelectField's. */
    labelAside?: ReactNode;
    className?: string;
}

/**
 * Takes the cursor to a field (scrolled into view) and flashes it, each time `token` changes: a warning's button, pressed again and
 * again (as FixHere flashes a warning shown).
 */
function useFocusToken(id: string, token: number | undefined) {
    useEffect(() => {
        // Once per token (lib/flash.ts): back on its tab, it isn't focused and flashed again.
        if (!token || alreadyShown(token)) return;
        // Just after: the tab it's on may only now be showing.
        const timer = setTimeout(() => {
            const input = document.getElementById(id);
            if (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) {
                input.scrollIntoView({ block: "center", behavior: "smooth" });
                input.focus({ preventScroll: true });
                flash(input, "is-flashed");
            }
            markShown(token);
        });
        return () => clearTimeout(timer);
    }, [token, id]);
}

/** A text field that saves when you leave it (or press Enter, except in multi-line fields). */
export function TextField(props: Readonly<TextFieldProps>) {
    const id = useId();
    const [draft, setDraft] = useState(props.value);
    const [invalid, setInvalid] = useState<string | null>(null);
    const save = useSave(props.onSave);
    const [focused, setFocused] = useState(false);
    // Ready to type over: focused, its text selected (only when it first shows). Just after showing,
    // so a window opening around it has shown itself and taken focus first.
    useEffect(() => {
        if (!props.selectOnOpen) return;
        const timer = setTimeout(() => {
            const input = document.getElementById(id);
            if (input instanceof HTMLInputElement) {
                input.focus();
                input.select();
            }
        });
        return () => clearTimeout(timer);
    }, [props.selectOnOpen, id]);
    useFocusToken(id, props.focusToken);
    // Show the latest value from the server (it updates live), except while it's being typed in,
    // while it saves, or after it wasn't accepted.
    const editing = draft !== props.value;
    useEffect(() => {
        if (!focused && save.state.kind !== "error" && save.state.kind !== "saving") setDraft(props.value);
    }, [props.value, focused, save.state.kind]);
    // A draft's field: what's typed and can't be taken holds its Save back (the draft still has what it had before).
    const drafts = useContext(DraftContext);
    const typed = props.multiline ? draft : draft.trim();
    const typedInvalid = Boolean(props.live && typed !== props.value && props.validate?.(typed));
    const report = drafts?.report;
    useEffect(() => {
        if (!report) return;
        report(id, typedInvalid);
        return () => report(id, false);
    }, [report, id, typedInvalid]);

    const commit = async () => {
        const value = props.multiline ? draft : draft.trim();
        if (value === props.value) {
            setDraft(props.value);
            save.clear();
            return;
        }
        const problem = props.validate?.(value) ?? null;
        setInvalid(problem);
        if (problem) return;
        await save.run(value);
    };

    const common = {
        id,
        value: draft,
        disabled: props.disabled,
        placeholder: props.placeholder,
        inputMode: props.inputMode,
        "aria-invalid": Boolean(invalid ?? (save.state.kind === "error")) || undefined,
        onChange: (e: { target: { value: string } }) => {
            setDraft(e.target.value);
            setInvalid(null);
            if (!props.live) return;
            // A draft's field: each valid change as it's typed, emptied too when it may be (one that can't be taken waits, holding
            // the draft's Save back; leaving it says what's wrong).
            const value = props.multiline ? e.target.value : e.target.value.trim();
            if (value !== props.value && !props.validate?.(value)) void props.onSave(value);
        },
        onFocus: () => setFocused(true),
        onBlur: () => {
            setFocused(false);
            void commit();
        },
    };
    return (
        <Frame id={id} label={props.label} hideLabel={props.hideLabel} hint={typeof props.hint === "function" ? props.hint(draft) : props.hint} save={save} error={invalid} className={props.className} labelAside={props.labelAside}>
            {props.multiline ? (
                <textarea {...common} rows={4} className={clsx("input resize-y", props.warning && "is-warning")} />
            ) : (
                <div className="relative flex items-center">
                    {props.prefix ? <span className="pointer-events-none absolute left-(--field-px) text-(length:--field-text) text-muted">{props.prefix}</span> : null}
                    <input
                        {...common}
                        className={clsx("input", props.prefix ? "pl-[calc(var(--field-px)+1.1em)]" : null, props.suffix ? "pr-20" : null, props.warning && "is-warning", editing && "border-brand-500")}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") e.currentTarget.blur();
                            if (e.key === "Escape") {
                                // Undo the typing only (not close the window it's in).
                                e.preventDefault();
                                setDraft(props.value);
                                setInvalid(null);
                                save.clear();
                            }
                        }}
                    />
                    {props.suffix ? <span className="pointer-events-none absolute right-(--field-px) text-(length:--label-text) text-muted">{props.suffix}</span> : null}
                </div>
            )}
        </Frame>
    );
}

export interface SaveButtonFieldProps {
    label: string;
    value: string;
    onSave: (value: string) => Promise<unknown>;
    disabled?: boolean;
    /** Checked before saving: a message stops the save and shows. */
    validate?: (value: string) => string | null;
    /** Asked before saving (e.g. useConfirm): false, it isn't saved, and what's typed stays. */
    confirm?: (value: string) => Promise<boolean>;
    /** Under it; a function is given what is typed so far (e.g. what changing it would affect). */
    hint?: ReactNode | ((typed: string) => ReactNode);
    /** As TextField's: highlighted while a warning is about it; each new token takes the cursor to it. */
    warning?: boolean;
    focusToken?: number;
    /** What's typed and not saved, kept outside it (its window: switching tabs keeps it, closing asks), and told of each change (null: nothing unsaved). */
    draft?: string | null;
    onDraft?: (draft: string | null) => void;
    /** On its label's line, on the right (e.g. what it's for), as SelectField's. */
    labelAside?: ReactNode;
    className?: string;
}

/**
 * A text field for a change that matters (an item number): typing shows Save and Cancel inside
 * it, and only Save (or Enter) saves it. Leaving it keeps what's typed (the buttons and its border
 * show it isn't saved). Escape or Cancel puts it back. Its saved value still updates live while
 * nothing's typed.
 */
export function SaveButtonField(props: Readonly<SaveButtonFieldProps>) {
    const id = useId();
    // What's typed, or null when nothing is: then it shows the saved value as it is (it updates live,
    // and there's nothing to catch up, so the buttons never show for a moment on a new value).
    const [typed, setTyped] = useState<string | null>(props.draft ?? null);
    const [invalid, setInvalid] = useState<string | null>(null);
    const save = useSave(props.onSave);
    useFocusToken(id, props.focusToken);
    const draft = typed ?? props.value;
    const unsaved = typed !== null && typed.trim() !== props.value;
    const type = (next: string) => {
        setTyped(next);
        props.onDraft?.(next.trim() === props.value ? null : next);
    };
    const done = () => {
        setTyped(null);
        props.onDraft?.(null);
    };

    const cancel = () => {
        done();
        setInvalid(null);
        save.clear();
    };
    const commit = async () => {
        const value = draft.trim();
        if (value === props.value) return cancel();
        const problem = props.validate?.(value) ?? null;
        setInvalid(problem);
        if (problem) return;
        if (props.confirm && !(await props.confirm(value))) return;
        if (await save.run(value)) done();
    };
    const hint = typeof props.hint === "function" ? props.hint(draft) : props.hint;
    const editable = !props.disabled;
    return (
        <Frame
            id={id}
            label={props.label}
            hint={hint}
            save={save}
            error={invalid}
            className={props.className}
            labelAside={props.labelAside}
        >
            <div className="relative flex items-center">
                <input
                    id={id}
                    value={draft}
                    disabled={props.disabled}
                    aria-invalid={Boolean(invalid ?? (save.state.kind === "error")) || undefined}
                    className={clsx("input", unsaved && editable && "pr-36", props.warning && "is-warning", unsaved && "border-brand-500")}
                    onChange={(e) => {
                        type(e.target.value);
                        setInvalid(null);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") void commit();
                        if (e.key === "Escape") {
                            // Undo the typing only (not close the window it's in).
                            e.preventDefault();
                            cancel();
                        }
                    }}
                />
                {unsaved && editable ? (
                    <span className="absolute right-1.5 flex gap-1">
                        <button type="button" className="btn-primary px-2.5 py-1 text-xs" disabled={save.state.kind === "saving"} onClick={() => void commit()}>
                            Save
                        </button>
                        <button type="button" className="btn-secondary px-2.5 py-1 text-xs" onClick={cancel}>
                            Cancel
                        </button>
                    </span>
                ) : null}
            </div>
        </Frame>
    );
}

/**
 * A switch that saves straight away, or (`ask`) one that asks first: flipping it calls `ask`, and the
 * switch stays as it is until the caller saves (and `checked` changes).
 */
export function ToggleField(
    props: Readonly<
        { label: string; checked: boolean; disabled?: boolean; hint?: ReactNode; /** Small, its words before the switch, for a field label's row (e.g. Override beside a tag). */ compact?: boolean } & (
            | { onSave: (checked: boolean) => Promise<unknown>; ask?: never }
            | { ask: (checked: boolean) => void; onSave?: never }
        )
    >,
) {
    const id = useId();
    const save = useSave((v) => props.onSave?.(v === "1") ?? Promise.resolve());
    const flip = (checked: boolean) => {
        if (props.ask) props.ask(checked);
        else void save.run(checked ? "1" : "0");
    };
    return (
        <div className="space-y-1">
            {/* Relative: the hidden checkbox is placed inside its label, so focusing it can't scroll an outer box (a window) to reach it. */}
            <label htmlFor={id} className={clsx("relative flex items-center", props.compact ? "flex-row-reverse gap-2" : "gap-3", props.disabled ? "cursor-default" : "cursor-pointer")}>
                <input
                    id={id}
                    type="checkbox"
                    className="peer sr-only"
                    checked={props.checked}
                    disabled={props.disabled || save.state.kind === "saving"}
                    onChange={(e) => flip(e.target.checked)}
                />
                <span
                    className={clsx(
                        "relative shrink-0 rounded-full bg-line transition peer-checked:bg-brand-600 peer-disabled:opacity-50 peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500/40 after:absolute after:top-0.5 after:left-0.5 after:rounded-full after:bg-white after:shadow after:transition",
                        props.compact ? "h-4 w-7 after:size-3 peer-checked:after:translate-x-3" : "h-5 w-9 after:size-4 peer-checked:after:translate-x-4",
                    )}
                />
                <span className={props.compact ? "text-(length:--label-text) text-muted" : "text-(length:--field-text)"}>{props.label}</span>
                <SaveMark save={save} />
            </label>
            {save.state.kind === "error" ? <p className="text-(length:--label-text) text-danger">{save.state.message}</p> : null}
            {save.state.kind !== "error" && props.hint ? <p className="pl-12 text-(length:--label-text) text-muted">{props.hint}</p> : null}
        </div>
    );
}

/** A dropdown that saves straight away. */
export function SelectField(
    props: Readonly<{
        label: string;
        value: string;
        options: { value: string; label: string }[];
        onSave: (value: string) => Promise<unknown>;
        disabled?: boolean;
        hint?: ReactNode;
        /** No visible label (what's beside it says what it is); screen readers still get `label`. */
        hideLabel?: boolean;
        className?: string;
        /** On the dropdown itself, e.g. muted for a value that means "not set". */
        selectClassName?: string;
        /** On the right of its label's row (a switch about it). */
        labelAside?: ReactNode;
    }>,
) {
    const id = useId();
    const save = useSave(props.onSave);
    return (
        <Frame id={id} label={props.label} hideLabel={props.hideLabel} hint={props.hint} save={save} className={props.className} labelAside={props.labelAside}>
            <select
                id={id}
                className={clsx("input", props.selectClassName)}
                value={props.value}
                disabled={props.disabled || save.state.kind === "saving"}
                onChange={(e) => void save.run(e.target.value)}
            >
                {props.options.map((o) => (
                    <option key={o.value} value={o.value}>
                        {o.label}
                    </option>
                ))}
            </select>
        </Frame>
    );
}

/** Buttons for one choice of a few, saving straight away (e.g. how a price is set). */
export function ChoiceField<T extends string>(
    props: Readonly<{
        label: string;
        value: T;
        /** `unavailable`: why it can't be chosen right now (greyed out, the reason said under it). `title`: its label in full, on hover. */
        options: { value: T; label: string; title?: string; hint?: string; unavailable?: string }[];
        onSave: (value: T) => Promise<unknown>;
        disabled?: boolean;
        /** Shown under it when the chosen option has no hint of its own. */
        hint?: ReactNode;
        /** On the right of its label's row (a switch about it). */
        labelAside?: ReactNode;
        /** No visible label (what's around it says what it is); screen readers still get `label`. */
        hideLabel?: boolean;
    }>,
) {
    const save = useSave((v) => props.onSave(v as T));
    const current = props.options.find((o) => o.value === props.value);
    const hint = current?.hint ?? props.hint;
    const reasons = [...new Set(props.options.flatMap((o) => (o.unavailable && o.value !== props.value ? [o.unavailable] : [])))];
    return (
        <fieldset className="space-y-1.5">
            <legend className={props.hideLabel ? "sr-only" : "field-label mb-1 flex w-full items-center gap-2"}>
                {props.label} <SaveMark save={save} />
                {props.labelAside ? <span className="ml-auto">{props.labelAside}</span> : null}
            </legend>
            <div className="flex flex-wrap gap-1 rounded-md border border-line bg-surface-2 p-1">
                {props.options.map((o) => (
                    <button
                        key={o.value}
                        type="button"
                        aria-pressed={o.value === props.value}
                        disabled={props.disabled || save.state.kind === "saving" || (Boolean(o.unavailable) && o.value !== props.value)}
                        title={(o.value === props.value ? undefined : o.unavailable) ?? o.title}
                        onClick={() => o.value !== props.value && void save.run(o.value)}
                        className={clsx(
                            "flex-1 cursor-pointer rounded px-3 py-(--field-py) text-(length:--field-text) whitespace-nowrap disabled:cursor-default",
                            o.value === props.value ? "bg-surface font-semibold shadow-sm" : "text-muted hover:text-ink",
                            o.unavailable && o.value !== props.value && "opacity-50 hover:text-muted",
                        )}
                    >
                        {o.label}
                    </button>
                ))}
            </div>
            {save.state.kind === "error" ? <p className="text-(length:--label-text) text-danger">{save.state.message}</p> : null}
            {save.state.kind !== "error" && hint ? <p className="text-(length:--label-text) text-muted">{hint}</p> : null}
            {reasons.map((reason) => (
                <p key={reason} className="text-(length:--label-text) text-muted">
                    {reason}
                </p>
            ))}
        </fieldset>
    );
}
